import React, {RefObject} from "react";
import {observer} from "mobx-react";
import classNames from "classnames";
import {DomEventSubscription} from "../../../../../utils/event-listener-registration";
import {OrbitComponentWithState} from "../../../base-components/orbit-component-with-state/orbit-component-with-state";
import {IStickyHandler} from "../../../sticky-handler/sticky-handler.interface";
import {LiveCasinoFilterButtonsBox} from "./boxes/live-casino-filter-buttons/live-casino-filter-buttons.box";
import {LiveCasinoNavbarScrollBox} from "./boxes/live-casino-navbar-scroll/live-casino-navbar-scroll.box";
import {LiveCasinoNavbarOverflowBox} from "./boxes/live-casino-navbar-overflow/live-casino-navbar-overflow.box";
import {STICKY_NAVBAR} from "./css-constants";
import {LiveCasinoNavbarBox} from "./boxes/live-casino-navbar/live-casino-navbar.box";
import {ButtonsActiveIndicatorBox} from "./boxes/buttons-active-indicator/buttons-active-indicator.box";
import {LiveCasinoArenaNavbarButtonProps} from "./live-casino-arena-navbar-button.component";
import {ThemeProvider} from "styled-components";
import {
  LiveTableTabConfiguration
} from "../../../../services/live-table-feeds-service/live-table-feeds.service.interface";

interface ICasinoLiveArenaNavbarProps {
  configs: LiveTableTabConfiguration[];
  stickyHandler: IStickyHandler;
  FilterButtonComponent: React.ComponentType<LiveCasinoArenaNavbarButtonProps>;
}

interface ICasinoLiveArenaNavbarState {
  activeButton: HTMLElement | null;
  activeIndicator: {
    width: number;
    position: number;
  };
}

export const LiveCasinoArenaNavbar = observer(class LiveCasinoArenaNavbar extends OrbitComponentWithState<
  ICasinoLiveArenaNavbarProps,
  ICasinoLiveArenaNavbarState
  > {
  private navbarRef = React.createRef<HTMLDivElement>();
  private buttonsRefs: Record<string, RefObject<HTMLDivElement>> = {};
  private scrollContainerRef = React.createRef<HTMLDivElement>();

  private _scrollEventHandler: DomEventSubscription | null = null;
  private _resizeEventHandler: DomEventSubscription | null = null;

  constructor(props: ICasinoLiveArenaNavbarProps) {
    super(props);

    this.state = {
      activeButton: null,
      activeIndicator: {
        width: 0,
        position: 0,
      }
    };

    // Create a ref for each category button
    this.buttonsRefs = this.props.configs.toDictionary(config => config.headerConfiguration.filter, () => React.createRef<HTMLDivElement>())
  }



  componentDidMount() {
    super.componentDidMount();


    if (!this.services.device.isMobile) {
      this._updateActiveIndicatorPosition();

      this._resizeEventHandler = this.services.window.subscribeTo.resize(
        this._updateActiveIndicatorPosition
      );

      this.reaction(() => this.services.liveTableFeeds.currentArenaTabConfiguration, () => {
        this._updateActiveIndicatorPosition();
      });
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this._resizeEventHandler && this._resizeEventHandler.dispose();
    this._scrollEventHandler && this._scrollEventHandler.dispose();
    this.props.stickyHandler && this.props.stickyHandler.dispose();
  }

  private _renderActiveIndicator() {
    if (this.services.device.isMobile) {
      return null;
    }

    return (
      <ButtonsActiveIndicatorBox
        className={"cy-buttons-active-indicator"}
        activeIndicatorWidth={this.state.activeIndicator.width}
        activeIndicatorPosition={this.state.activeIndicator.position}
      />
    );
  }

  private _onFilterClicked = async (tabId: string)  => {
    await this.services.liveTableFeeds.setCurrentArenaTab(tabId);
  };



  private _updateActiveIndicatorPosition = () => {
    const activeButton = this.buttonsRefs[this.services.liveTableFeeds.currentArenaTabConfiguration?.headerConfiguration?.filter]?.current;
    const scrollContainer = this.scrollContainerRef.current;

    // Use this width offset to make the active indicator larger than the actual button
    const widthOffset = 100;

    if (scrollContainer && activeButton) {
      this.setState({
        activeIndicator: {
          width: activeButton.offsetWidth + widthOffset,
          position:
            activeButton.getBoundingClientRect().left -
            widthOffset / 2 -
            scrollContainer.getBoundingClientRect().left,
        },
      });
    }
  };



  render() {
    const FilterButtonComponent = this.props.FilterButtonComponent || React.Fragment;
    return (
      <ThemeProvider theme={this.services.theme.currentTheme}>
        <LiveCasinoNavbarBox
          ref={this.navbarRef}
          className={classNames("cy-live-casino-navbar", {
            [STICKY_NAVBAR]: this.props.stickyHandler.isSticky,
          })}
          stickOffset={this.services.header.headerHeight}
        >
          <LiveCasinoNavbarOverflowBox
            className={"cy-live-casino-navbar-overflow"}
          >
            <LiveCasinoNavbarScrollBox
              className={"cy-live-casino-navbar-scroll"}
              ref={this.scrollContainerRef}
            >
              <LiveCasinoFilterButtonsBox
                className={"cy-live-casino-filter-buttons"}
              >
                {this.props.configs.map((tabConfig) => {
                  return (
                    tabConfig.gridConfiguration.gamesId.length === 0 ? null :
                    <FilterButtonComponent
                      key={tabConfig.headerConfiguration.filter}
                      labelTranslationKey={tabConfig.gridConfiguration.title}
                      filter={tabConfig.headerConfiguration.filter}
                      onClick={async () => await this._onFilterClicked(tabConfig.headerConfiguration.filter)}
                      buttonLabelRef={this.buttonsRefs[tabConfig.headerConfiguration.filter]}
                      seoPage={tabConfig.headerConfiguration.seoPage}
                      isActiveTab={tabConfig.headerConfiguration?.filter?.toLowerCase() === this.services.liveTableFeeds.currentArenaTabConfiguration?.headerConfiguration?.filter?.toLowerCase()}
                    />
                  );

                })}
              </LiveCasinoFilterButtonsBox>
              {this._renderActiveIndicator()}
            </LiveCasinoNavbarScrollBox>
          </LiveCasinoNavbarOverflowBox>
        </LiveCasinoNavbarBox>
      </ThemeProvider>
    );
  }
});
